import { usePageTitle } from '../hooks/usePageTitle';
import React, { useState, useEffect } from 'react';
import { commsAPI, clientAPI, userAPI } from '../lib/api';
import { formatDate } from '../lib/utils';
import {
  Mail,
  MessageCircle,
  Send,
  FileText,
  History,
  BarChart3,
  Plus,
  Trash2,
  Edit2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Search,
  RefreshCw,
  Loader2,
  Copy,
  Settings,
  Shield,
  Key,
  Phone,
  AtSign,
  Zap,
  TestTube2,
  Eye,
  EyeOff,
  Save,
  DollarSign,
  ChevronDown,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';

const CHANNEL_COLORS = {
  email: 'bg-blue-100 text-blue-700',
  whatsapp: 'bg-emerald-100 text-emerald-700',
  both: 'bg-violet-100 text-violet-700',
};

const STATUS_COLORS = {
  sent: 'bg-emerald-100 text-emerald-700',
  mock_sent: 'bg-amber-100 text-amber-700',
  delivered: 'bg-emerald-100 text-emerald-700',
  failed: 'bg-red-100 text-red-700',
  queued: 'bg-slate-100 text-slate-700',
};

const CATEGORY_LABELS = {
  invoice: 'Invoice',
  visit_reminder: 'Visit Reminder',
  phase_update: 'Phase Update',
  ticket_update: 'Ticket Update',
  custom: 'Custom',
};

const CATEGORY_COLORS = [
  'from-blue-500 to-blue-600',
  'from-emerald-500 to-emerald-600',
  'from-violet-500 to-violet-600',
  'from-amber-500 to-amber-600',
  'from-rose-500 to-rose-600',
];

export const CommunicationsPage = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  usePageTitle('Communications');
  const [stats, setStats] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [logs, setLogs] = useState([]);
  const [clients, setClients] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logsLoading, setLogsLoading] = useState(false);

  // Compose state
  const [composeChannel, setComposeChannel] = useState('email');
  const [composeEmail, setComposeEmail] = useState('');
  const [composePhone, setComposePhone] = useState('');
  const [composeName, setComposeName] = useState('');
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [composeWhatsappBody, setComposeWhatsappBody] = useState('');
  const [composeClientId, setComposeClientId] = useState('');
  const [composeRecipientType, setComposeRecipientType] = useState('client');
  const [composeStaffId, setComposeStaffId] = useState('');
  const [composeAdminCc, setComposeAdminCc] = useState(true);
  const [sending, setSending] = useState(false);

  // Template dialog
  const [isTemplateOpen, setIsTemplateOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: '', channel: 'both', subject: '', body: '', category: 'custom', variables: '',
  });

  // Filters
  const [logChannel, setLogChannel] = useState('all');
  const [logCategory, setLogCategory] = useState('all');

  // Config state
  const [config, setConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [emailForm, setEmailForm] = useState({ sender_email: '', api_key: '' });
  const [whatsappForm, setWhatsappForm] = useState({ whatsapp_number: '', account_sid: '', auth_token: '' });
  const [savingEmail, setSavingEmail] = useState(false);
  const [savingWhatsapp, setSavingWhatsapp] = useState(false);
  const [bankForm, setBankForm] = useState({ bank_name: '', account_name: '', account_number: '', ifsc_code: '', branch: '', upi_id: '' });
  const [savingBank, setSavingBank] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testingWhatsapp, setTestingWhatsapp] = useState(false);
  const [testEmailAddr, setTestEmailAddr] = useState('');
  const [testPhone, setTestPhone] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [showSid, setShowSid] = useState(false);
  const [showToken, setShowToken] = useState(false);

  // Gmail state
  const [gmailForm, setGmailForm] = useState({ gmail_email: '', gmail_app_password: '', gmail_display_name: 'Steward Healthcare' });
  const [savingGmail, setSavingGmail] = useState(false);
  const [testingGmail, setTestingGmail] = useState(false);
  const [showGmailPass, setShowGmailPass] = useState(false);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    try {
      const [statsRes, logsRes, clientsRes, staffRes] = await Promise.all([
        commsAPI.getStats(),
        commsAPI.getLogs({ limit: 50 }),
        clientAPI.getAll(),
        userAPI.getAll().catch(() => ({ data: [] })),
      ]);
      setStats(statsRes.data);
      setLogs(logsRes.data);
      setClients(clientsRes.data);
      setStaffList((staffRes.data || []).filter(u => u.role === 'staff' || u.role === 'manager' || u.role === 'admin'));

      // Load templates from new message_templates API
      try {
        const { messageTemplateAPI: mtAPI } = await import('../lib/api');
        const tplRes = await mtAPI.getAll();
        setTemplates(tplRes.data);
      } catch {
        setTemplates([]);
      }
    } catch {
      toast.error('Failed to load communication data');
    } finally {
      setLoading(false);
    }
  };

  const loadConfig = async () => {
    setConfigLoading(true);
    try {
      const [configRes, bankRes] = await Promise.all([
        commsAPI.getConfig(),
        commsAPI.getBankDetails().catch(() => ({ data: {} })),
      ]);
      setConfig(configRes.data);
      setEmailForm({
        sender_email: configRes.data.email_sender || '',
        api_key: '',
      });
      setWhatsappForm({
        whatsapp_number: configRes.data.whatsapp_number || '',
        account_sid: '',
        auth_token: '',
      });
      setBankForm({
        bank_name: bankRes.data.bank_name || '',
        account_name: bankRes.data.account_name || '',
        account_number: bankRes.data.account_number || '',
        ifsc_code: bankRes.data.ifsc_code || '',
        branch: bankRes.data.branch || '',
        upi_id: bankRes.data.upi_id || '',
      });
      setGmailForm({
        gmail_email: configRes.data.gmail_email || '',
        gmail_app_password: '',
        gmail_display_name: configRes.data.gmail_display_name || 'Steward Healthcare',
      });
    } catch {
      toast.error('Failed to load configuration');
    } finally {
      setConfigLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'settings' && !config) loadConfig();
  }, [activeTab]);

  const loadLogs = async () => {
    setLogsLoading(true);
    try {
      const params = { limit: 50 };
      if (logChannel !== 'all') params.channel = logChannel;
      if (logCategory !== 'all') params.category = logCategory;
      const res = await commsAPI.getLogs(params);
      setLogs(res.data);
    } catch {
      toast.error('Failed to load logs');
    } finally {
      setLogsLoading(false);
    }
  };

  useEffect(() => {
    if (!loading) loadLogs();
  }, [logChannel, logCategory]);

  const handleSeedTemplates = async () => {
    try {
      const res = await commsAPI.seedTemplates();
      toast.success(res.data.message);
      const t = await commsAPI.getTemplates();
      setTemplates(t.data);
    } catch {
      toast.error('Failed to seed templates');
    }
  };

  const handleSendMessage = async () => {
    if (!composeBody.trim() && composeChannel !== 'whatsapp') { toast.error('Email body is required'); return; }
    if (composeChannel !== 'email' && !composeWhatsappBody.trim() && !composeBody.trim()) { toast.error('Message body required'); return; }
    setSending(true);
    try {
      const payload = {
        recipient_type: composeRecipientType,
        recipient_ids: [],
        custom_emails: [],
        custom_phones: [],
        channel: composeChannel,
        subject: composeSubject,
        email_body: composeBody,
        whatsapp_body: composeWhatsappBody || composeBody.replace(/<[^>]*>/g, ''),
        admin_cc: composeAdminCc,
      };
      if (composeRecipientType === 'client' && composeClientId) {
        payload.recipient_ids = [composeClientId];
      } else if (composeRecipientType === 'staff' && composeStaffId) {
        payload.recipient_ids = [composeStaffId];
      } else if (composeRecipientType === 'custom') {
        if (composeEmail) payload.custom_emails = [composeEmail];
        if (composePhone) payload.custom_phones = [composePhone];
      }

      const { messagingAPI: msgAPI } = await import('../lib/api');
      const res = await msgAPI.send(payload);
      toast.success(res.data.message || 'Message sent!');
      if (res.data.errors?.length > 0) {
        res.data.errors.forEach(e => toast.warning(e));
      }
      setComposeBody(''); setComposeSubject(''); setComposeWhatsappBody('');
      loadLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name.trim() || !templateForm.body.trim()) {
      toast.error('Name and body are required');
      return;
    }
    try {
      const payload = {
        ...templateForm,
        variables: templateForm.variables.split(',').map(v => v.trim()).filter(Boolean),
      };
      if (editingTemplate) {
        await commsAPI.updateTemplate(editingTemplate.id, payload);
        toast.success('Template updated');
      } else {
        await commsAPI.createTemplate(payload);
        toast.success('Template created');
      }
      setIsTemplateOpen(false);
      const t = await commsAPI.getTemplates();
      setTemplates(t.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await commsAPI.deleteTemplate(id);
      toast.success('Template deleted');
      setTemplates(templates.filter(t => t.id !== id));
    } catch {
      toast.error('Failed to delete template');
    }
  };

  const openEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name,
      channel: template.channel,
      subject: template.subject || '',
      body: template.body,
      category: template.category || 'custom',
      variables: (template.variables || []).join(', '),
    });
    setIsTemplateOpen(true);
  };

  const handleSaveEmailConfig = async () => {
    if (!emailForm.sender_email.trim()) { toast.error('Sender email is required'); return; }
    setSavingEmail(true);
    try {
      const res = await commsAPI.updateEmailConfig(emailForm);
      toast.success(res.data.message);
      loadConfig();
      loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save email config');
    } finally {
      setSavingEmail(false);
    }
  };

  const handleSaveWhatsappConfig = async () => {
    if (!whatsappForm.whatsapp_number.trim()) { toast.error('WhatsApp number is required'); return; }
    setSavingWhatsapp(true);
    try {
      const res = await commsAPI.updateWhatsappConfig(whatsappForm);
      toast.success(res.data.message);
      loadConfig();
      loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save WhatsApp config');
    } finally {
      setSavingWhatsapp(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmailAddr.trim()) { toast.error('Enter a test email address'); return; }
    setTestingEmail(true);
    try {
      const res = await commsAPI.testEmail({ test_email: testEmailAddr });
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Test email failed');
    } finally {
      setTestingEmail(false);
    }
  };

  const handleTestWhatsapp = async () => {
    if (!testPhone.trim()) { toast.error('Enter a test phone number'); return; }
    setTestingWhatsapp(true);
    try {
      const res = await commsAPI.testWhatsapp({ test_phone: testPhone });
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Test WhatsApp failed');
    } finally {
      setTestingWhatsapp(false);
    }
  };

  const handleClientSelect = (clientId) => {
    setComposeClientId(clientId);
    const client = clients.find(c => c.id === clientId);
    if (client) {
      setComposeEmail(client.email || '');
      setComposePhone(client.phone || '');
      setComposeName(client.organization_name || '');
    }
  };

  const handleStaffSelect = (staffId) => {
    setComposeStaffId(staffId);
    const staff = staffList.find(s => s.id === staffId);
    if (staff) {
      setComposeEmail(staff.email || '');
      setComposePhone(staff.phone || '');
      setComposeName(staff.name || '');
    }
  };

  const handleSaveBankDetails = async () => {
    setSavingBank(true);
    try {
      const res = await commsAPI.updateBankDetails(bankForm);
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save bank details');
    } finally {
      setSavingBank(false);
    }
  };

  const handleSaveGmailConfig = async () => {
    setSavingGmail(true);
    try {
      const res = await commsAPI.updateGmailConfig(gmailForm);
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save Gmail config');
    } finally {
      setSavingGmail(false);
    }
  };

  const handleTestGmail = async () => {
    setTestingGmail(true);
    try {
      const payload = { gmail_email: gmailForm.gmail_email, gmail_app_password: gmailForm.gmail_app_password };
      const res = await commsAPI.testGmail(payload);
      if (res.data.success) toast.success(res.data.message);
      else toast.error(res.data.error || 'Connection failed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Test failed');
    } finally {
      setTestingGmail(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in" data-testid="communications-page">
      {/* Colorful Header */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-[#0f4c81] via-[#1a6cb4] to-[#2196F3] p-6 text-white">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage:'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.3) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.2) 0%, transparent 50%)'}} />
        <div className="relative z-10">
          <h1 className="text-2xl font-bold">Communications Hub</h1>
          <p className="text-blue-100 text-sm mt-1">Manage email & WhatsApp messaging, templates, and configuration</p>
        </div>
      </div>

      {/* Integration Status Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card className={`relative overflow-hidden border-0 shadow-md ${stats?.email_configured ? 'bg-gradient-to-br from-emerald-50 to-teal-50' : 'bg-gradient-to-br from-amber-50 to-orange-50'}`} data-testid="email-status-card">
          <div className={`absolute top-0 left-0 w-1.5 h-full ${stats?.email_configured ? 'bg-emerald-500' : 'bg-amber-500'}`} />
          <CardContent className="p-5 flex items-center gap-4">
            <div className={`p-3 rounded-xl ${stats?.email_configured ? 'bg-emerald-100' : 'bg-amber-100'}`}>
              <Mail className={`w-6 h-6 ${stats?.email_configured ? 'text-emerald-600' : 'text-amber-600'}`} />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-sm">Email (SendGrid)</p>
              <p className="text-xs text-muted-foreground mt-0.5">Sender: {config?.email_sender || stats?.sender_email || 'Not configured'}</p>
            </div>
            <Badge className={`${stats?.email_configured ? 'bg-emerald-500 hover:bg-emerald-600 text-white' : 'bg-amber-500 hover:bg-amber-600 text-white'} px-3 py-1`}>
              {stats?.email_configured ? 'Active' : 'Mock Mode'}
            </Badge>
          </CardContent>
        </Card>

        <Card className={`relative overflow-hidden border-0 shadow-md ${stats?.whatsapp_configured ? 'bg-gradient-to-br from-emerald-50 to-teal-50' : 'bg-gradient-to-br from-amber-50 to-orange-50'}`} data-testid="whatsapp-status-card">
          <div className={`absolute top-0 left-0 w-1.5 h-full ${stats?.whatsapp_configured ? 'bg-emerald-500' : 'bg-amber-500'}`} />
          <CardContent className="p-5 flex items-center gap-4">
            <div className={`p-3 rounded-xl ${stats?.whatsapp_configured ? 'bg-emerald-100' : 'bg-amber-100'}`}>
              <MessageCircle className={`w-6 h-6 ${stats?.whatsapp_configured ? 'text-emerald-600' : 'text-amber-600'}`} />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-sm">WhatsApp (Twilio)</p>
              <p className="text-xs text-muted-foreground mt-0.5">Number: {config?.whatsapp_number || 'Not configured'}</p>
            </div>
            <Badge className={`${stats?.whatsapp_configured ? 'bg-emerald-500 hover:bg-emerald-600 text-white' : 'bg-amber-500 hover:bg-amber-600 text-white'} px-3 py-1`}>
              {stats?.whatsapp_configured ? 'Active' : 'Mock Mode'}
            </Badge>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5 bg-slate-100">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard" className="data-[state=active]:bg-white data-[state=active]:text-blue-700 data-[state=active]:shadow-sm">
            <BarChart3 className="w-4 h-4 mr-1.5" /> Dashboard
          </TabsTrigger>
          <TabsTrigger value="compose" data-testid="tab-compose" className="data-[state=active]:bg-white data-[state=active]:text-blue-700 data-[state=active]:shadow-sm">
            <Send className="w-4 h-4 mr-1.5" /> Send
          </TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates" className="data-[state=active]:bg-white data-[state=active]:text-blue-700 data-[state=active]:shadow-sm">
            <FileText className="w-4 h-4 mr-1.5" /> Templates
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history" className="data-[state=active]:bg-white data-[state=active]:text-blue-700 data-[state=active]:shadow-sm">
            <History className="w-4 h-4 mr-1.5" /> History
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings" className="data-[state=active]:bg-white data-[state=active]:text-blue-700 data-[state=active]:shadow-sm">
            <Settings className="w-4 h-4 mr-1.5" /> Settings
          </TabsTrigger>
        </TabsList>

        {/* DASHBOARD TAB */}
        <TabsContent value="dashboard" className="space-y-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Card className="p-5 text-center border-0 shadow-md bg-gradient-to-br from-blue-50 to-indigo-50">
              <p className="text-3xl font-bold text-blue-700">{stats?.total_messages || 0}</p>
              <p className="text-sm text-blue-600/70 font-medium mt-1">Total Messages</p>
            </Card>
            <Card className="p-5 text-center border-0 shadow-md bg-gradient-to-br from-sky-50 to-cyan-50">
              <div className="flex items-center justify-center gap-2">
                <Mail className="w-5 h-5 text-sky-600" />
                <p className="text-3xl font-bold text-sky-700">{stats?.total_emails || 0}</p>
              </div>
              <p className="text-sm text-sky-600/70 font-medium mt-1">Emails Sent</p>
            </Card>
            <Card className="p-5 text-center border-0 shadow-md bg-gradient-to-br from-emerald-50 to-teal-50">
              <div className="flex items-center justify-center gap-2">
                <MessageCircle className="w-5 h-5 text-emerald-600" />
                <p className="text-3xl font-bold text-emerald-700">{stats?.total_whatsapp || 0}</p>
              </div>
              <p className="text-sm text-emerald-600/70 font-medium mt-1">WhatsApp Sent</p>
            </Card>
            <Card className="p-5 text-center border-0 shadow-md bg-gradient-to-br from-violet-50 to-purple-50">
              <p className="text-3xl font-bold text-violet-700">{stats?.total_templates || 0}</p>
              <p className="text-sm text-violet-600/70 font-medium mt-1">Templates</p>
            </Card>
          </div>

          {/* By Category - Colorful */}
          <Card className="border-0 shadow-md">
            <CardHeader>
              <CardTitle className="text-lg">Messages by Category</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {stats?.by_category && Object.entries(stats.by_category).map(([cat, count], idx) => (
                  <div key={cat} className={`p-4 rounded-xl text-center text-white bg-gradient-to-br ${CATEGORY_COLORS[idx % CATEGORY_COLORS.length]} shadow-sm`}>
                    <p className="text-2xl font-bold">{count}</p>
                    <p className="text-xs font-medium opacity-90 mt-1">{CATEGORY_LABELS[cat] || cat}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Recent Messages */}
          <Card className="border-0 shadow-md">
            <CardHeader>
              <CardTitle className="text-lg">Recent Messages</CardTitle>
            </CardHeader>
            <CardContent>
              {logs.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No messages sent yet</p>
              ) : (
                <div className="space-y-2">
                  {logs.slice(0, 5).map((log) => (
                    <div key={log.id} className="flex items-center gap-3 p-3 border rounded-lg hover:bg-slate-50 transition-colors">
                      {log.channel === 'email' ? (
                        <div className="p-2 rounded-lg bg-blue-100"><Mail className="w-4 h-4 text-blue-600" /></div>
                      ) : (
                        <div className="p-2 rounded-lg bg-emerald-100"><MessageCircle className="w-4 h-4 text-emerald-600" /></div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {log.subject || log.body?.substring(0, 60)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          To: {log.recipient_name || log.recipient_email || log.recipient_phone}
                        </p>
                      </div>
                      <Badge className={STATUS_COLORS[log.status] || 'bg-slate-100'}>
                        {log.status === 'mock_sent' ? 'Mock' : log.status}
                      </Badge>
                      <span className="text-xs text-muted-foreground">{formatDate(log.created_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* COMPOSE TAB */}
        <TabsContent value="compose">
          <Card className="border-0 shadow-md">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-t-lg">
              <CardTitle className="text-lg flex items-center gap-2"><Send className="w-5 h-5 text-blue-600" /> Compose Message</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 p-6">
              {/* Row 1: Channel, Recipient Type, Admin CC */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Channel *</Label>
                  <Select value={composeChannel} onValueChange={setComposeChannel}>
                    <SelectTrigger data-testid="compose-channel"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="whatsapp">WhatsApp</SelectItem>
                      <SelectItem value="both">Both</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Send To *</Label>
                  <Select value={composeRecipientType} onValueChange={v => { setComposeRecipientType(v); setComposeClientId(''); setComposeStaffId(''); setComposeEmail(''); setComposePhone(''); setComposeName(''); }}>
                    <SelectTrigger data-testid="compose-recipient-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="client">Client</SelectItem>
                      <SelectItem value="staff">Staff</SelectItem>
                      <SelectItem value="custom">Custom Email/Phone</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {composeRecipientType === 'client' && (
                  <div className="space-y-2">
                    <Label>Select Client</Label>
                    <Select value={composeClientId} onValueChange={handleClientSelect}>
                      <SelectTrigger data-testid="compose-client"><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>
                        {clients.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.organization_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {composeRecipientType === 'staff' && (
                  <div className="space-y-2">
                    <Label>Select Staff</Label>
                    <Select value={composeStaffId} onValueChange={handleStaffSelect}>
                      <SelectTrigger data-testid="compose-staff"><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>
                        {staffList.map(s => (
                          <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {composeRecipientType === 'custom' && (
                  <div className="space-y-2">
                    <Label>Email / Phone</Label>
                    <div className="flex gap-2">
                      {composeChannel !== 'whatsapp' && <Input value={composeEmail} onChange={e => setComposeEmail(e.target.value)} placeholder="email@example.com" className="flex-1" data-testid="compose-email" />}
                      {composeChannel !== 'email' && <Input value={composePhone} onChange={e => setComposePhone(e.target.value)} placeholder="+91XXXXXXXXXX" className="flex-1" data-testid="compose-phone" />}
                    </div>
                  </div>
                )}
                <div className="space-y-2">
                  <Label>&nbsp;</Label>
                  <label className="flex items-center gap-2 h-10 px-3 border rounded-md bg-amber-50 border-amber-200 cursor-pointer">
                    <input type="checkbox" checked={composeAdminCc} onChange={e => setComposeAdminCc(e.target.checked)} className="rounded border-gray-300" />
                    <span className="text-sm font-medium text-amber-700">Admin CC</span>
                  </label>
                </div>
              </div>

              {/* Recipient info */}
              {composeName && (
                <div className="flex items-center gap-4 p-2 bg-blue-50 rounded-lg text-sm">
                  <span><strong>To:</strong> {composeName}</span>
                  {composeEmail && <span>| {composeEmail}</span>}
                  {composePhone && <span>| {composePhone}</span>}
                </div>
              )}

              {/* Email fields */}
              {composeChannel !== 'whatsapp' && (
                <>
                  <div className="space-y-2">
                    <Label>Email Subject</Label>
                    <Input value={composeSubject} onChange={(e) => setComposeSubject(e.target.value)} placeholder="Message subject" data-testid="compose-subject" />
                  </div>
                  <div className="space-y-2">
                    <Label>Email Body *</Label>
                    <Textarea value={composeBody} onChange={(e) => setComposeBody(e.target.value)} rows={5} placeholder="Write your email content..." data-testid="compose-body" />
                  </div>
                </>
              )}

              {/* WhatsApp fields */}
              {composeChannel !== 'email' && (
                <div className="space-y-2">
                  <Label>WhatsApp Message *</Label>
                  <Textarea value={composeWhatsappBody} onChange={(e) => setComposeWhatsappBody(e.target.value)} rows={4} placeholder="Write your WhatsApp message..." data-testid="compose-whatsapp-body" />
                </div>
              )}

              {/* Warnings */}
              {!config?.gmail_configured && !stats?.email_configured && composeChannel !== 'whatsapp' && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 text-amber-700 text-sm border border-amber-200">
                  <AlertTriangle className="w-4 h-4 shrink-0" /> No email service configured. Set up Gmail SMTP or SendGrid in Settings tab.
                </div>
              )}
              {!stats?.whatsapp_configured && composeChannel !== 'email' && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 text-amber-700 text-sm border border-amber-200">
                  <AlertTriangle className="w-4 h-4 shrink-0" /> WhatsApp is not configured. Configure Twilio in Settings tab.
                </div>
              )}

              <Button onClick={handleSendMessage} disabled={sending} className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700" data-testid="send-btn">
                {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Send Message
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TEMPLATES TAB */}
        <TabsContent value="templates" className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {templates.length} template(s) - These templates are used for automated event notifications
            </p>
            <Button variant="outline" onClick={async () => {
              try {
                const { messageTemplateAPI: mtAPI } = await import('../lib/api');
                const res = await mtAPI.getAll();
                setTemplates(res.data);
                toast.success('Templates loaded');
              } catch { toast.error('Failed'); }
            }} data-testid="refresh-templates-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> Reload
            </Button>
          </div>

          {templates.length === 0 ? (
            <Card className="border-0 shadow-md">
              <CardContent className="py-12 text-center text-muted-foreground">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No templates yet. They will be auto-created when you first open this page.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {templates.map((template) => (
                <Card key={template.id} className={`border-0 shadow-sm overflow-hidden ${!template.is_active ? 'opacity-50' : ''}`}>
                  <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50"
                    onClick={() => setEditingTemplate(editingTemplate === template.id ? null : template.id)}>
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-50 rounded-lg"><FileText className="w-4 h-4 text-blue-600" /></div>
                      <div>
                        <p className="font-semibold text-sm">{template.name}</p>
                        <div className="flex gap-1.5 mt-1">
                          {(template.channels || []).map(ch => (
                            <Badge key={ch} className={ch === 'email' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}>{ch}</Badge>
                          ))}
                          {(template.recipients || []).map(r => (
                            <Badge key={r} className="bg-slate-100 text-slate-600">{r}</Badge>
                          ))}
                          {template.admin_cc && <Badge className="bg-amber-100 text-amber-700">Admin CC</Badge>}
                          {!template.is_active && <Badge className="bg-red-100 text-red-700">Disabled</Badge>}
                        </div>
                      </div>
                    </div>
                    <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${editingTemplate === template.id ? 'rotate-180' : ''}`} />
                  </div>

                  {editingTemplate === template.id && (
                    <div className="border-t p-4 bg-slate-50/50 space-y-3">
                      <div className="grid grid-cols-3 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Channels</Label>
                          <div className="flex gap-2">
                            {['email', 'whatsapp'].map(ch => (
                              <label key={ch} className="flex items-center gap-1">
                                <input type="checkbox" checked={(template.channels || []).includes(ch)}
                                  onChange={async (e) => {
                                    const newCh = e.target.checked ? [...(template.channels || []), ch] : (template.channels || []).filter(c => c !== ch);
                                    const { messageTemplateAPI: mtAPI } = await import('../lib/api');
                                    await mtAPI.update(template.id, { channels: newCh });
                                    setTemplates(t => t.map(tp => tp.id === template.id ? { ...tp, channels: newCh } : tp));
                                  }} className="rounded" />
                                <span className="text-xs capitalize">{ch}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Recipients</Label>
                          <div className="flex gap-2">
                            {['staff', 'client', 'admin'].map(r => (
                              <label key={r} className="flex items-center gap-1">
                                <input type="checkbox" checked={(template.recipients || []).includes(r)}
                                  onChange={async (e) => {
                                    const newR = e.target.checked ? [...(template.recipients || []), r] : (template.recipients || []).filter(c => c !== r);
                                    const { messageTemplateAPI: mtAPI } = await import('../lib/api');
                                    await mtAPI.update(template.id, { recipients: newR });
                                    setTemplates(t => t.map(tp => tp.id === template.id ? { ...tp, recipients: newR } : tp));
                                  }} className="rounded" />
                                <span className="text-xs capitalize">{r}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                        <div className="space-y-1">
                          <label className="flex items-center gap-2">
                            <input type="checkbox" checked={template.admin_cc}
                              onChange={async (e) => {
                                const { messageTemplateAPI: mtAPI } = await import('../lib/api');
                                await mtAPI.update(template.id, { admin_cc: e.target.checked });
                                setTemplates(t => t.map(tp => tp.id === template.id ? { ...tp, admin_cc: e.target.checked } : tp));
                              }} className="rounded" />
                            <span className="text-xs font-medium">Admin CC</span>
                          </label>
                          <label className="flex items-center gap-2 mt-2">
                            <input type="checkbox" checked={template.is_active}
                              onChange={async (e) => {
                                const { messageTemplateAPI: mtAPI } = await import('../lib/api');
                                await mtAPI.update(template.id, { is_active: e.target.checked });
                                setTemplates(t => t.map(tp => tp.id === template.id ? { ...tp, is_active: e.target.checked } : tp));
                                toast.success(e.target.checked ? 'Template activated' : 'Template disabled');
                              }} className="rounded" />
                            <span className="text-xs font-medium">Active</span>
                          </label>
                        </div>
                      </div>

                      <div className="space-y-1">
                        <Label className="text-xs">Email Subject</Label>
                        <Input value={template.email_subject || ''} className="text-sm h-8"
                          onChange={(e) => setTemplates(t => t.map(tp => tp.id === template.id ? { ...tp, email_subject: e.target.value } : tp))} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Email Body (HTML)</Label>
                        <Textarea value={template.email_body || ''} rows={4} className="text-xs font-mono"
                          onChange={(e) => setTemplates(t => t.map(tp => tp.id === template.id ? { ...tp, email_body: e.target.value } : tp))} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">WhatsApp Body</Label>
                        <Textarea value={template.whatsapp_body || ''} rows={3} className="text-xs"
                          onChange={(e) => setTemplates(t => t.map(tp => tp.id === template.id ? { ...tp, whatsapp_body: e.target.value } : tp))} />
                      </div>
                      <p className="text-xs text-muted-foreground">Variables: {'{recipient_name}'}, {'{client_name}'}, {'{staff_names}'}, {'{visit_title}'}, {'{task_title}'}, {'{visit_date}'}, {'{visit_time}'}, {'{due_date}'}, {'{email}'}, {'{password}'}</p>
                      <Button size="sm" onClick={async () => {
                        try {
                          const { messageTemplateAPI: mtAPI } = await import('../lib/api');
                          await mtAPI.update(template.id, {
                            email_subject: template.email_subject,
                            email_body: template.email_body,
                            whatsapp_body: template.whatsapp_body,
                          });
                          toast.success('Template saved');
                        } catch { toast.error('Failed to save'); }
                      }} data-testid={`save-template-${template.id}`}>
                        <Save className="w-3.5 h-3.5 mr-1" /> Save Template
                      </Button>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* HISTORY TAB */}
        <TabsContent value="history" className="space-y-4">
          <Card className="border-0 shadow-md">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <CardTitle className="text-lg">Message History</CardTitle>
                <div className="flex items-center gap-2">
                  <Select value={logChannel} onValueChange={setLogChannel}>
                    <SelectTrigger className="w-36" data-testid="log-channel-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Channels</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={logCategory} onValueChange={setLogCategory}>
                    <SelectTrigger className="w-40" data-testid="log-category-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      <SelectItem value="invoice">Invoice</SelectItem>
                      <SelectItem value="visit_reminder">Visit Reminder</SelectItem>
                      <SelectItem value="phase_update">Phase Update</SelectItem>
                      <SelectItem value="ticket_update">Ticket Update</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="sm" onClick={loadLogs} disabled={logsLoading}>
                    <RefreshCw className={`w-4 h-4 ${logsLoading ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {logs.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No messages found</p>
              ) : (
                <div className="overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Channel</TableHead>
                        <TableHead>Recipient</TableHead>
                        <TableHead>Subject / Body</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {logs.map((log) => (
                        <TableRow key={log.id} className="hover:bg-slate-50">
                          <TableCell>
                            {log.channel === 'email' ? (
                              <div className="flex items-center gap-1.5"><div className="p-1 rounded bg-blue-100"><Mail className="w-3 h-3 text-blue-600" /></div> <span className="text-xs">Email</span></div>
                            ) : (
                              <div className="flex items-center gap-1.5"><div className="p-1 rounded bg-emerald-100"><MessageCircle className="w-3 h-3 text-emerald-600" /></div> <span className="text-xs">WhatsApp</span></div>
                            )}
                          </TableCell>
                          <TableCell>
                            <p className="text-sm font-medium">{log.recipient_name || '-'}</p>
                            <p className="text-xs text-muted-foreground">{log.recipient_email || log.recipient_phone}</p>
                          </TableCell>
                          <TableCell className="max-w-[250px]">
                            <p className="text-sm truncate">{log.subject || log.body?.substring(0, 50)}</p>
                          </TableCell>
                          <TableCell><Badge variant="outline" className="text-xs">{CATEGORY_LABELS[log.category] || log.category}</Badge></TableCell>
                          <TableCell><Badge className={STATUS_COLORS[log.status] || 'bg-slate-100'}>{log.status === 'mock_sent' ? 'Mock' : log.status}</Badge></TableCell>
                          <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{formatDate(log.created_at)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* SETTINGS TAB - Configuration Master */}
        <TabsContent value="settings" className="space-y-6" data-testid="settings-tab">
          {configLoading ? (
            <div className="flex items-center justify-center h-40"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
          ) : (
            <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Email Configuration */}
              <Card className="border-0 shadow-lg overflow-hidden" data-testid="email-config-card">
                <div className="bg-gradient-to-r from-blue-600 to-sky-500 p-5 text-white">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-white/20 rounded-xl backdrop-blur-sm">
                      <Mail className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg">Email Configuration</h3>
                      <p className="text-blue-100 text-sm">SendGrid API Integration</p>
                    </div>
                  </div>
                </div>
                <CardContent className="p-5 space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-2.5 h-2.5 rounded-full ${config?.email_api_key_set ? 'bg-emerald-500' : 'bg-amber-500'} animate-pulse`} />
                    <span className={`text-sm font-medium ${config?.email_api_key_set ? 'text-emerald-600' : 'text-amber-600'}`}>
                      {config?.email_api_key_set ? 'API Key Configured' : 'Not Configured (Mock Mode)'}
                    </span>
                  </div>

                  <div className="space-y-2">
                    <Label className="flex items-center gap-1.5"><AtSign className="w-3.5 h-3.5 text-blue-500" /> Sender Email Address *</Label>
                    <Input
                      value={emailForm.sender_email}
                      onChange={(e) => setEmailForm({ ...emailForm, sender_email: e.target.value })}
                      placeholder="noreply@yourdomain.com"
                      data-testid="email-sender-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="flex items-center gap-1.5"><Key className="w-3.5 h-3.5 text-blue-500" /> SendGrid API Key</Label>
                    <div className="relative">
                      <Input
                        type={showApiKey ? 'text' : 'password'}
                        value={emailForm.api_key}
                        onChange={(e) => setEmailForm({ ...emailForm, api_key: e.target.value })}
                        placeholder={config?.email_api_key_masked || 'SG.xxxx...'}
                        data-testid="email-apikey-input"
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        onClick={() => setShowApiKey(!showApiKey)}
                      >
                        {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {config?.email_api_key_masked && (
                      <p className="text-xs text-muted-foreground">Current: {config.email_api_key_masked}</p>
                    )}
                  </div>

                  <div className="flex gap-2 pt-2">
                    <Button onClick={handleSaveEmailConfig} disabled={savingEmail} className="flex-1 bg-gradient-to-r from-blue-600 to-sky-500 hover:from-blue-700 hover:to-sky-600" data-testid="save-email-config-btn">
                      {savingEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Save Email Config
                    </Button>
                  </div>

                  <div className="border-t pt-4 mt-4">
                    <Label className="text-sm font-medium flex items-center gap-1.5 mb-2"><TestTube2 className="w-3.5 h-3.5 text-blue-500" /> Test Email</Label>
                    <div className="flex gap-2">
                      <Input
                        value={testEmailAddr}
                        onChange={(e) => setTestEmailAddr(e.target.value)}
                        placeholder="test@example.com"
                        className="flex-1"
                        data-testid="test-email-input"
                      />
                      <Button variant="outline" onClick={handleTestEmail} disabled={testingEmail} data-testid="test-email-btn">
                        {testingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* WhatsApp Configuration */}
              <Card className="border-0 shadow-lg overflow-hidden" data-testid="whatsapp-config-card">
                <div className="bg-gradient-to-r from-emerald-600 to-teal-500 p-5 text-white">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-white/20 rounded-xl backdrop-blur-sm">
                      <MessageCircle className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg">WhatsApp Configuration</h3>
                      <p className="text-emerald-100 text-sm">Twilio API Integration</p>
                    </div>
                  </div>
                </div>
                <CardContent className="p-5 space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-2.5 h-2.5 rounded-full ${config?.whatsapp_sid_set ? 'bg-emerald-500' : 'bg-amber-500'} animate-pulse`} />
                    <span className={`text-sm font-medium ${config?.whatsapp_sid_set ? 'text-emerald-600' : 'text-amber-600'}`}>
                      {config?.whatsapp_sid_set ? 'Twilio Configured' : 'Not Configured (Mock Mode)'}
                    </span>
                  </div>

                  <div className="space-y-2">
                    <Label className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-emerald-500" /> Sender WhatsApp Number *</Label>
                    <Input
                      value={whatsappForm.whatsapp_number}
                      onChange={(e) => setWhatsappForm({ ...whatsappForm, whatsapp_number: e.target.value })}
                      placeholder="+14155238886"
                      data-testid="whatsapp-number-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="flex items-center gap-1.5"><Key className="w-3.5 h-3.5 text-emerald-500" /> Twilio Account SID</Label>
                    <div className="relative">
                      <Input
                        type={showSid ? 'text' : 'password'}
                        value={whatsappForm.account_sid}
                        onChange={(e) => setWhatsappForm({ ...whatsappForm, account_sid: e.target.value })}
                        placeholder={config?.whatsapp_sid_masked || 'ACxxxx...'}
                        data-testid="whatsapp-sid-input"
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        onClick={() => setShowSid(!showSid)}
                      >
                        {showSid ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {config?.whatsapp_sid_masked && (
                      <p className="text-xs text-muted-foreground">Current: {config.whatsapp_sid_masked}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label className="flex items-center gap-1.5"><Shield className="w-3.5 h-3.5 text-emerald-500" /> Twilio Auth Token</Label>
                    <div className="relative">
                      <Input
                        type={showToken ? 'text' : 'password'}
                        value={whatsappForm.auth_token}
                        onChange={(e) => setWhatsappForm({ ...whatsappForm, auth_token: e.target.value })}
                        placeholder={config?.whatsapp_token_masked || 'Token...'}
                        data-testid="whatsapp-token-input"
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        onClick={() => setShowToken(!showToken)}
                      >
                        {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {config?.whatsapp_token_masked && (
                      <p className="text-xs text-muted-foreground">Current: {config.whatsapp_token_masked}</p>
                    )}
                  </div>

                  <div className="flex gap-2 pt-2">
                    <Button onClick={handleSaveWhatsappConfig} disabled={savingWhatsapp} className="flex-1 bg-gradient-to-r from-emerald-600 to-teal-500 hover:from-emerald-700 hover:to-teal-600" data-testid="save-whatsapp-config-btn">
                      {savingWhatsapp ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Save WhatsApp Config
                    </Button>
                  </div>

                  <div className="border-t pt-4 mt-4">
                    <Label className="text-sm font-medium flex items-center gap-1.5 mb-2"><TestTube2 className="w-3.5 h-3.5 text-emerald-500" /> Test WhatsApp</Label>
                    <div className="flex gap-2">
                      <Input
                        value={testPhone}
                        onChange={(e) => setTestPhone(e.target.value)}
                        placeholder="+91XXXXXXXXXX"
                        className="flex-1"
                        data-testid="test-whatsapp-input"
                      />
                      <Button variant="outline" onClick={handleTestWhatsapp} disabled={testingWhatsapp} data-testid="test-whatsapp-btn">
                        {testingWhatsapp ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Gmail SMTP Configuration */}
            <Card className="border-0 shadow-lg overflow-hidden" data-testid="gmail-config-card">
              <div className="bg-gradient-to-r from-red-500 to-orange-400 p-5 text-white">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-white/20 rounded-xl backdrop-blur-sm">
                    <Mail className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">Gmail SMTP Configuration</h3>
                    <p className="text-red-100 text-sm">Send emails directly via Gmail App Password</p>
                  </div>
                </div>
              </div>
              <CardContent className="p-5 space-y-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-2.5 h-2.5 rounded-full ${config?.gmail_configured ? 'bg-emerald-500' : 'bg-amber-500'} animate-pulse`} />
                  <span className={`text-sm font-medium ${config?.gmail_configured ? 'text-emerald-600' : 'text-amber-600'}`}>
                    {config?.gmail_configured ? 'Gmail Configured' : 'Not Configured'}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1.5"><AtSign className="w-3.5 h-3.5 text-red-500" /> Gmail Address *</Label>
                    <Input
                      value={gmailForm.gmail_email}
                      onChange={(e) => setGmailForm({ ...gmailForm, gmail_email: e.target.value })}
                      placeholder="youremail@gmail.com"
                      data-testid="gmail-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1.5"><Key className="w-3.5 h-3.5 text-red-500" /> App Password *</Label>
                    <div className="relative">
                      <Input
                        type={showGmailPass ? 'text' : 'password'}
                        value={gmailForm.gmail_app_password}
                        onChange={(e) => setGmailForm({ ...gmailForm, gmail_app_password: e.target.value })}
                        placeholder="xxxx xxxx xxxx xxxx"
                        data-testid="gmail-password-input"
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        onClick={() => setShowGmailPass(!showGmailPass)}
                      >
                        {showGmailPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {config?.gmail_email && <p className="text-xs text-muted-foreground">Current: {config.gmail_email}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1.5">Display Name</Label>
                    <Input
                      value={gmailForm.gmail_display_name}
                      onChange={(e) => setGmailForm({ ...gmailForm, gmail_display_name: e.target.value })}
                      placeholder="Steward Healthcare"
                      data-testid="gmail-display-name-input"
                    />
                  </div>
                </div>

                <p className="text-xs text-muted-foreground bg-amber-50 p-3 rounded-lg border border-amber-200">
                  To use Gmail SMTP, you need a Google App Password. Go to Google Account &gt; Security &gt; 2-Step Verification &gt; App passwords, and generate one for "Mail".
                </p>

                <div className="flex gap-2 pt-2">
                  <Button onClick={handleSaveGmailConfig} disabled={savingGmail} className="bg-gradient-to-r from-red-500 to-orange-400 hover:from-red-600 hover:to-orange-500" data-testid="save-gmail-config-btn">
                    {savingGmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Gmail Config
                  </Button>
                  <Button variant="outline" onClick={handleTestGmail} disabled={testingGmail || !gmailForm.gmail_email || !gmailForm.gmail_app_password} data-testid="test-gmail-btn">
                    {testingGmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <TestTube2 className="w-4 h-4 mr-2" />}
                    Test Connection
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Bank Details for Invoice Printing */}
            <Card className="border-0 shadow-lg overflow-hidden" data-testid="bank-details-card">
              <div className="bg-gradient-to-r from-indigo-600 to-violet-500 p-5 text-white">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-white/20 rounded-xl backdrop-blur-sm">
                    <DollarSign className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">Bank Details for Invoice</h3>
                    <p className="text-indigo-100 text-sm">These details will be printed on all invoice PDFs</p>
                  </div>
                </div>
              </div>
              <CardContent className="p-5">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Bank Name</Label>
                    <Input value={bankForm.bank_name} onChange={(e) => setBankForm({...bankForm, bank_name: e.target.value})} placeholder="e.g. State Bank of India" data-testid="bank-name-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Account Holder Name</Label>
                    <Input value={bankForm.account_name} onChange={(e) => setBankForm({...bankForm, account_name: e.target.value})} placeholder="e.g. Steward Healthcare India Pvt Ltd" data-testid="bank-account-name-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Account Number</Label>
                    <Input value={bankForm.account_number} onChange={(e) => setBankForm({...bankForm, account_number: e.target.value})} placeholder="e.g. 1234567890" data-testid="bank-account-number-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>IFSC Code</Label>
                    <Input value={bankForm.ifsc_code} onChange={(e) => setBankForm({...bankForm, ifsc_code: e.target.value})} placeholder="e.g. SBIN0001234" data-testid="bank-ifsc-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Branch</Label>
                    <Input value={bankForm.branch} onChange={(e) => setBankForm({...bankForm, branch: e.target.value})} placeholder="e.g. Panipat Main Branch" data-testid="bank-branch-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>UPI ID (optional)</Label>
                    <Input value={bankForm.upi_id} onChange={(e) => setBankForm({...bankForm, upi_id: e.target.value})} placeholder="e.g. steward@upi" data-testid="bank-upi-input" />
                  </div>
                </div>
                <Button onClick={handleSaveBankDetails} disabled={savingBank} className="mt-4 bg-gradient-to-r from-indigo-600 to-violet-500 hover:from-indigo-700 hover:to-violet-600" data-testid="save-bank-details-btn">
                  {savingBank ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Bank Details
                </Button>
              </CardContent>
            </Card>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Template Dialog */}
      <Dialog open={isTemplateOpen} onOpenChange={setIsTemplateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingTemplate ? 'Edit' : 'Create'} Template</DialogTitle>
            <DialogDescription>
              {editingTemplate ? 'Update the message template' : 'Create a new message template'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                  placeholder="Template name"
                  data-testid="template-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Channel</Label>
                <Select value={templateForm.channel} onValueChange={(v) => setTemplateForm({ ...templateForm, channel: v })}>
                  <SelectTrigger data-testid="template-channel"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">Email Only</SelectItem>
                    <SelectItem value="whatsapp">WhatsApp Only</SelectItem>
                    <SelectItem value="both">Both</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={templateForm.category} onValueChange={(v) => setTemplateForm({ ...templateForm, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="invoice">Invoice</SelectItem>
                    <SelectItem value="visit_reminder">Visit Reminder</SelectItem>
                    <SelectItem value="phase_update">Phase Update</SelectItem>
                    <SelectItem value="ticket_update">Ticket Update</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Variables (comma-separated)</Label>
                <Input
                  value={templateForm.variables}
                  onChange={(e) => setTemplateForm({ ...templateForm, variables: e.target.value })}
                  placeholder="client_name, date, amount"
                />
              </div>
            </div>
            {templateForm.channel !== 'whatsapp' && (
              <div className="space-y-2">
                <Label>Subject</Label>
                <Input
                  value={templateForm.subject}
                  onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })}
                  placeholder="Email subject with {{variables}}"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label>Body *</Label>
              <Textarea
                value={templateForm.body}
                onChange={(e) => setTemplateForm({ ...templateForm, body: e.target.value })}
                rows={5}
                placeholder="Message body with {{variable}} placeholders..."
                data-testid="template-body-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsTemplateOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveTemplate} data-testid="save-template-btn">
              {editingTemplate ? 'Update' : 'Create'} Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CommunicationsPage;
